import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  TaskDialog,
  Text,
  HStack,
  VStack,
  TextField,
  TextArea,
  SelectBox,
  RadioButton,
  FormControl,
  SubSectionTitle,
  Note,
  StatusIcon,
} from '@freee_jp/vibes';
import { JournalEntry } from '../types/journal';
import { createJob } from '../api/client';

// ---------------------------------------------------------------------------
// Types / props
// ---------------------------------------------------------------------------

export type CorrectionAction = 'correct' | 'reject' | 'reprocess';

export interface CorrectionModalProps {
  isOpen: boolean;
  entry: JournalEntry | null;
  deptOptions: { name: string; value: string }[];
  onClose: () => void;
  onSuccess: (instructionId: string, action: CorrectionAction) => void;
}

// Common 税区分 (tax code) values used by freee. The Hub expects the integer
// tax code, but we label them in Japanese for the user.
const TAX_CODE_OPTIONS = [
  { name: '課対仕入10%',     value: '136' },
  { name: '課対仕入8%(軽)',  value: '137' },
  { name: '課対仕入8%',      value: '135' },
  { name: '対象外',         value: '108' },
  { name: '非課税仕入',      value: '109' },
  { name: '免税',           value: '110' },
];

const ACTION_LABELS: Record<CorrectionAction, { label: string; desc: string; danger?: boolean }> = {
  correct:   { label: '修正',       desc: '科目・税区分・部門などを修正します' },
  reject:    { label: '却下',       desc: '仕訳を登録対象から除外します', danger: true },
  reprocess: { label: '再処理依頼', desc: 'Claudeに再処理を依頼します' },
};

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const fmt = (n: number) => '¥' + n.toLocaleString('ja-JP');

const S = {
  summaryBox: {
    background: '#fafaf8',
    border: '1px solid #edebea',
    borderRadius: '8px',
    padding: '12px 16px',
    marginBottom: '16px',
  } as React.CSSProperties,
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    padding: '4px 0',
    fontSize: '0.8125rem',
  } as React.CSSProperties,
  label: {
    color: '#8a8580',
    fontSize: '0.75rem',
    flexShrink: 0,
    marginRight: '16px',
  } as React.CSSProperties,
  value: {
    color: '#252525',
    fontSize: '0.8125rem',
    fontWeight: 500,
    textAlign: 'right' as const,
    wordBreak: 'break-all' as const,
  } as React.CSSProperties,
  actionCard: (selected: boolean, danger?: boolean) => ({
    flex: 1,
    padding: '10px 12px',
    border: `2px solid ${selected ? (danger ? '#c33939' : '#2e7cf6') : '#edebea'}`,
    borderRadius: '8px',
    background: selected ? (danger ? '#fdecec' : '#ebf3ff') : '#fff',
    cursor: 'pointer',
    transition: 'background 0.15s, border-color 0.15s',
  }),
  sectionSpacing: { marginTop: '16px' } as React.CSSProperties,
  helperText: {
    fontSize: '0.6875rem',
    color: '#8a8580',
    marginTop: '4px',
  } as React.CSSProperties,
  errorBox: {
    padding: '10px 12px',
    marginTop: '12px',
    border: '1px solid #c33939',
    background: '#fdecec',
    borderRadius: '6px',
    fontSize: '0.8125rem',
    color: '#c33939',
  } as React.CSSProperties,
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const CorrectionModal: React.FC<CorrectionModalProps> = ({
  isOpen,
  entry,
  deptOptions,
  onClose,
  onSuccess,
}) => {
  const [action, setAction] = useState<CorrectionAction>('correct');
  // Checkbox-based field re-examination request. Instead of specifying new
  // values (which is error-prone when the original has mixed tax rates / multi
  // details), user flags which fields Claude should re-read carefully from PDF.
  const [flags, setFlags] = useState<Record<string, boolean>>({});
  const [issueDate, setIssueDate] = useState<string>('');
  const [dueDate, setDueDate] = useState<string>('');
  const [note, setNote] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen || !entry) return;
    setAction('correct');
    setFlags({});
    setIssueDate(entry.issueDate ?? '');
    setDueDate(entry.dueDate ?? '');
    setNote('');
    setErrorMsg(null);
    setSubmitting(false);
  }, [isOpen, entry?.id]);

  const issueDateChanged = !!entry && issueDate !== (entry.issueDate ?? '');
  const dueDateChanged = !!entry && dueDate !== (entry.dueDate ?? '');
  const anyFieldFlagged = Object.values(flags).some(Boolean);
  const toggleFlag = (key: string) => setFlags((prev) => ({ ...prev, [key]: !prev[key] }));

  // Each flag represents a request to Claude: "please re-examine this field in the PDF".
  // We never send a specific new value for account / tax / section / item / amount /
  // description / detail_structure — Claude is expected to extract the correct
  // value from the source invoice. Dates are the only exception because dates are
  // often unambiguous and can be user-specified directly.
  const FIELD_FLAGS: Array<{ key: string; label: string; hint?: string }> = [
    { key: 'account_item', label: '勘定科目', hint: '借方科目の選定を再確認' },
    { key: 'tax_code', label: '税区分', hint: '各明細の税率・軽減税率区分を再確認' },
    { key: 'section', label: '部門', hint: '納品先店舗の判定を再確認' },
    { key: 'item', label: '品目', hint: '品目IDまたは未設定の判定' },
    { key: 'amount', label: '金額', hint: '税込/税抜・明細金額の読み取り' },
    { key: 'description', label: '摘要', hint: '備考欄の文言' },
    { key: 'detail_structure', label: '明細構成', hint: '行数・分割単位（税率/店舗別）' },
    { key: 'receipts', label: '証憑紐付け', hint: '添付ファイルの過不足' },
  ];

  // 2026-07-08 jobs基盤へ切替: 旧 POST /api/instructions は channel-poller (デスクトップ
  // セッション随伴) しか配送先がなく、ミニPC Runner (jobs のみ claim) には一切届かない
  // 不達バグだった。type=user_message / origin=ui の job として POST /api/jobs に発行する
  // (CommandDock と同一経路 = minipc-01 が claim → 実行 → succeeded)。
  const handleSubmit = useCallback(async () => {
    if (!entry || submitting) return;
    setSubmitting(true);
    setErrorMsg(null);
    try {
      const ref = entry.refNumber || `arica:${entry.rowid}`;
      const fieldsToReexamine = Object.entries(flags)
        .filter(([, v]) => v)
        .map(([k]) => k);
      const fieldLabels = FIELD_FLAGS
        .filter((f) => fieldsToReexamine.includes(f.key))
        .map((f) => f.label);

      let text: string;
      if (action === 'correct') {
        // "re-examine these fields from the source PDF" — no specific values for
        // account/tax/section/etc. Claude re-reads the PDF and updates the proposal.
        text = [
          `【仕訳修正指示】対象請求書: ${ref} (${entry.partnerName} / ¥${entry.grandTotal.toLocaleString('ja-JP')})`,
          fieldLabels.length > 0
            ? `再確認して修正すべき項目: ${fieldLabels.join('・')} — 元PDFを再度慎重に読み取り、正しい値に更新すること (具体値の指定なし = PDF が真値)`
            : '',
          issueDateChanged ? `発生日を ${issueDate || '(未設定)'} に変更すること` : '',
          dueDateChanged ? `支払期日を ${dueDate || '(未設定)'} に変更すること` : '',
          note ? `ユーザーコメント (判断材料): ${note}` : '',
          `完了条件: 該当 deal_proposal を修正内容で更新/再生成する。freee への書込は行わない (登録は UI 承認起点 = R2)。`,
        ].filter(Boolean).join('\n');
      } else if (action === 'reject') {
        text = [
          `【仕訳却下指示】対象請求書: ${ref} (${entry.partnerName}) を登録対象から除外してください。`,
          `理由: ${note || '却下'}`,
          `完了条件: 該当 deal_proposal を rejected にする。freee への書込は行わない。`,
        ].join('\n');
      } else {
        text = [
          `【再処理依頼】対象請求書: ${ref} (${entry.partnerName}) を再処理してください。`,
          `指示: ${note || '再処理依頼'}`,
          `完了条件: PDF 再読取 → proposal 再生成。freee への書込は行わない (登録は UI 承認起点 = R2)。`,
        ].join('\n');
      }

      const res = await createJob(
        'user_message',
        {
          text,
          source: 'journal_verification_correction',
          context: {
            action,
            entry_id: entry.id,
            arica_rowid: entry.rowid,
            ref_number: ref,
            partner_name: entry.partnerName,
            ...(action === 'correct' ? { fields_to_reexamine: fieldsToReexamine } : {}),
            ...(issueDateChanged ? { issue_date: issueDate || null } : {}),
            ...(dueDateChanged ? { due_date: dueDate || null } : {}),
            reason: note || null,
          },
        },
        { created_by: 'freeeman-web' },
      );

      onSuccess(res.id || 'unknown', action);
    } catch (err: any) {
      setErrorMsg(err?.message ?? String(err));
    } finally {
      setSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry, action, flags, issueDate, dueDate, issueDateChanged, dueDateChanged, note, submitting, onSuccess]);

  if (!entry) return null;

  const first = entry.details[0];
  const primaryLabel =
    action === 'correct'   ? '修正指示を送信' :
    action === 'reject'    ? '却下指示を送信' :
                             '再処理を依頼';
  const cfg = ACTION_LABELS[action];

  return (
    <TaskDialog
      isOpen={isOpen}
      title={`仕訳修正 - ${entry.partnerName}`}
      primaryButtonLabel={primaryLabel}
      closeButtonLabel="キャンセル"
      onPrimaryAction={handleSubmit}
      onRequestClose={onClose}
      disabled={submitting}
      suspend={action === 'correct'
        ? (!anyFieldFlagged && !note && !issueDateChanged && !dueDateChanged)
        : !note}
      danger={cfg.danger}
      shouldCloseOnOverlayClickOrEsc={!submitting}
      headerSideContent={
        <Text size={0.75} color="burnt">
          #{entry.rowid}
        </Text>
      }
    >
      {/* Current values summary */}
      <div style={S.summaryBox}>
        <SubSectionTitle headlineLevel={4}>現在の仕訳</SubSectionTitle>
        <div style={S.row}>
          <span style={S.label}>借方科目</span>
          <span style={S.value}>{first?.accountName ?? '--'}</span>
        </div>
        <div style={S.row}>
          <span style={S.label}>税区分</span>
          <span style={S.value}>{first?.taxCodeName ?? '--'}</span>
        </div>
        <div style={S.row}>
          <span style={S.label}>部門</span>
          <span style={S.value}>{first?.sectionName ?? '--'}</span>
        </div>
        <div style={S.row}>
          <span style={S.label}>金額</span>
          <span style={{ ...S.value, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
            {fmt(entry.grandTotal)}
          </span>
        </div>
        <div style={S.row}>
          <span style={S.label}>ステータス</span>
          <span>
            <StatusIcon
              type={
                entry.status === 'approved'     ? 'success' :
                entry.status === 'needs_review' ? 'warning' :
                entry.status === 'blocked'      ? 'error'   :
                                                  'progress'
              }
            >
              {entry.status === 'approved'     ? '完了(承認済)' :
               entry.status === 'needs_review' ? '対応待ち(承認)' :
               entry.status === 'blocked'      ? '除外(却下)' :
                                                '処理中'}
            </StatusIcon>
          </span>
        </div>
      </div>

      {/* Action selector */}
      <div>
        <SubSectionTitle headlineLevel={4}>アクション</SubSectionTitle>
        <HStack gap={0.5}>
          {(['correct', 'reject', 'reprocess'] as CorrectionAction[]).map((k) => {
            const c = ACTION_LABELS[k];
            const selected = action === k;
            return (
              <div
                key={k}
                style={S.actionCard(selected, c.danger)}
                onClick={() => setAction(k)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setAction(k); }}
              >
                <HStack gap={0.5} alignItems="center">
                  <RadioButton
                    name="correction-action"
                    checked={selected}
                    onChange={() => setAction(k)}
                    small
                  />
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '0.8125rem', color: c.danger && selected ? '#c33939' : '#252525' }}>
                      {c.label}
                    </div>
                    <div style={{ fontSize: '0.6875rem', color: '#8a8580' }}>{c.desc}</div>
                  </div>
                </HStack>
              </div>
            );
          })}
        </HStack>
      </div>

      {/* Field-level re-examination checkboxes + optional date overrides */}
      {action === 'correct' && (
        <div style={S.sectionSpacing}>
          <SubSectionTitle headlineLevel={4}>再確認を依頼する項目</SubSectionTitle>
          <div style={{ fontSize: '0.7rem', color: '#8a8580', marginBottom: '8px' }}>
            チェックを入れた項目は Claude が PDF を再度慎重に読み取ります。具体値の指定は不要 — 下のコメント欄に判断材料を記入してください。
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '6px 12px',
              marginBottom: '12px',
            }}
          >
            {FIELD_FLAGS.map((f) => (
              <label
                key={f.key}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '8px',
                  padding: '6px 8px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  background: flags[f.key] ? '#ebf3ff' : 'transparent',
                  border: `1px solid ${flags[f.key] ? '#2e7cf6' : '#e5e4e2'}`,
                }}
              >
                <input
                  type="checkbox"
                  checked={!!flags[f.key]}
                  onChange={() => toggleFlag(f.key)}
                  style={{ marginTop: '2px', cursor: 'pointer' }}
                />
                <div>
                  <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#252525' }}>{f.label}</div>
                  <div style={{ fontSize: '0.6875rem', color: '#8a8580' }}>{f.hint}</div>
                </div>
              </label>
            ))}
          </div>
          <VStack gap={0.5}>
            <FormControl
              label={issueDateChanged ? '発生日 *変更予定' : '発生日 (任意で直接変更)'}
              fieldId="fld-issue-date"
            >
              <input
                id="fld-issue-date"
                type="date"
                value={issueDate}
                onChange={(e) => setIssueDate(e.target.value)}
                style={{
                  padding: '6px 8px',
                  fontSize: '0.875rem',
                  border: `1px solid ${issueDateChanged ? '#f0b000' : '#d5d3d0'}`,
                  borderRadius: '4px',
                  background: issueDateChanged ? '#fff8e1' : '#fff',
                  fontFamily: 'inherit',
                }}
              />
            </FormControl>
            <FormControl
              label={dueDateChanged ? '支払期日 *変更予定' : '支払期日 (任意で直接変更)'}
              fieldId="fld-due-date"
            >
              <input
                id="fld-due-date"
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                style={{
                  padding: '6px 8px',
                  fontSize: '0.875rem',
                  border: `1px solid ${dueDateChanged ? '#f0b000' : '#d5d3d0'}`,
                  borderRadius: '4px',
                  background: dueDateChanged ? '#fff8e1' : '#fff',
                  fontFamily: 'inherit',
                }}
              />
            </FormControl>
          </VStack>
        </div>
      )}

      {/* Memo field - always visible */}
      <div style={S.sectionSpacing}>
        <FormControl
          label={action === 'correct'
            ? (anyFieldFlagged ? 'コメント *必須 (Claudeへの判断材料)' : 'コメント')
            : '理由 *必須'}
          fieldId="fld-note"
        >
          <TextArea
            id="fld-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            width="full"
            height={4}
            placeholder={
              action === 'correct'  ? '例: 「税率が複数あるはず。明細ごとに軽減8%と10%を分けて」「部門が喜乃字屋になっているが実際は信州屋渋谷」' :
              action === 'reject'   ? '却下の理由を記入してください' :
                                      'Claudeへの指示内容を記入してください'
            }
          />
        </FormControl>
      </div>

      {/* Submit error */}
      {errorMsg && <div style={S.errorBox}>送信エラー: {errorMsg}</div>}

      {/* Footer helper */}
      <div style={{ marginTop: '12px' }}>
        <Note>
          送信すると Hub API ({'POST /api/jobs'}, type=user_message) にジョブとして登録され、
          ミニPC常駐 Runner が受信して処理します (進捗は #jobs で追跡可)。
          指示の送信が完了した時点で「処理中(指示対応中)」表示に切り替わります。
        </Note>
      </div>
    </TaskDialog>
  );
};

export default CorrectionModal;
